import { History, Calculator, Dices, FileDown, LayoutDashboard, LogOut, BarChart3, ListChecks, Shield, Dna, Wand2, Sparkles, Sigma, Layers, Bell, CreditCard, Combine, LifeBuoy } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useAuth } from "@/hooks/use-auth";
import { useIsAdmin } from "@/hooks/use-admin";
import { usePlan } from "@/hooks/use-plan";
import { cn } from "@/lib/utils";

type Item = {
  title: string;
  url: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
  tier?: "pro" | "premium";
};

const items: Item[] = [
  { title: "Dashboard", url: "/app", icon: LayoutDashboard, end: true },
  { title: "Histórico de resultados", url: "/app/historico", icon: History },
  { title: "Frequência", url: "/app/frequencia", icon: BarChart3 },
  { title: "Probabilidades", url: "/app/probabilidades", icon: Calculator },
  { title: "Gerador", url: "/app/gerador", icon: Dices },
  { title: "Estatísticas avançadas", url: "/app/estatisticas", icon: Sigma, tier: "pro" },
  { title: "Gerador inteligente", url: "/app/gerador-inteligente", icon: Wand2, tier: "pro" },
  { title: "DNA do jogo", url: "/app/dna", icon: Dna, tier: "pro" },
  { title: "Combinações", url: "/app/combinacoes", icon: Combine, tier: "pro" },
  { title: "Bolões", url: "/app/boloes", icon: Layers, tier: "premium" },
  { title: "Coach IA", url: "/app/coach", icon: Sparkles, tier: "premium" },
  { title: "Meus jogos", url: "/app/jogos", icon: ListChecks },
  { title: "Exportar", url: "/app/exportar", icon: FileDown },
  { title: "Alertas", url: "/app/alertas", icon: Bell },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const { signOut, user } = useAuth();
  const { isAdmin } = useIsAdmin();
  const { has } = usePlan();

  return (
    <Sidebar collapsible="icon" className="border-r border-lt-border">
      <SidebarContent>
        <div className="flex h-14 items-center border-b border-lt-border px-4">
          {!collapsed ? (
            <NavLink to="/" className="font-serif text-lg font-semibold text-foreground">
              Loto<em className="not-italic text-primary">Terminal</em>
            </NavLink>
          ) : (
            <NavLink to="/" className="font-serif text-lg font-semibold text-primary">L</NavLink>
          )}
        </div>

        <SidebarGroup>
          <SidebarGroupLabel>Análise</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {items.map((item) => {
                const active = item.end
                  ? location.pathname === item.url
                  : location.pathname.startsWith(item.url);
                const locked = item.tier && !has(item.tier);
                return (
                  <SidebarMenuItem key={item.url}>
                    <SidebarMenuButton asChild isActive={active}>
                      <NavLink to={item.url} end={item.end} className="flex items-center gap-2">
                        <item.icon className="h-4 w-4 shrink-0" />
                        {!collapsed && (
                          <>
                            <span className="flex-1 truncate text-[13px]">{item.title}</span>
                            {item.tier && (
                              <span
                                className={cn(
                                  "rounded-full border px-1.5 py-px text-[9px] font-medium uppercase tracking-wider",
                                  locked
                                    ? "border-primary/40 bg-primary/10 text-primary"
                                    : "border-lt-teal/40 bg-lt-teal/10 text-lt-teal"
                                )}
                              >
                                {item.tier}
                              </span>
                            )}
                          </>
                        )}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {isAdmin && (
          <SidebarGroup>
            <SidebarGroupLabel>Administração</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={location.pathname.startsWith("/admin")}>
                    <NavLink to="/admin" className="flex items-center gap-2">
                      <Shield className="h-4 w-4 shrink-0" />
                      {!collapsed && <span className="flex-1 truncate text-[13px]">Painel admin</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-lt-border p-2">
      <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={location.pathname.startsWith("/app/assinatura")}>
              <NavLink to="/app/assinatura" className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 shrink-0" />
                {!collapsed && <span className="flex-1 truncate text-[13px]">Minha assinatura</span>}
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={location.pathname.startsWith("/app/suporte")}>
              <NavLink to="/app/suporte" className="flex items-center gap-2">
                <LifeBuoy className="h-4 w-4 shrink-0" />
                {!collapsed && <span className="flex-1 truncate text-[13px]">Suporte</span>}
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        {!collapsed && user && (
          <div className="mb-2 px-2 text-[11px] text-muted-foreground truncate">
            {user.user_metadata?.full_name || user.email}
          </div>
        )}
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={() => signOut()}>
              <LogOut className="h-4 w-4" />
              {!collapsed && <span className="text-[13px]">Sair</span>}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
